import React, { useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// Reissue modal — opened from PmsInvoiceRegister for CANCELLED invoices that
// haven't been replaced yet. Calls POST /api/v1/pms/invoices/:id/reissue.
// Optional narrative overrides; empty = mirror original.
//
// Props:
//   token, invoice, selectedProperty, onClose, onReissued (callback after success)
export default function InvoiceReissueModal({ token, invoice, selectedProperty, onClose, onReissued }) {
  const { isMobile } = useIsMobile();
  const [form, setForm] = useState({
    guest_name: invoice?.guest_name || '',
    customer_gstin: invoice?.customer_gstin || '',
    customer_state_code: invoice?.customer_state_code || '',
    place_of_supply: invoice?.place_of_supply || ''
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  // Corrected-amount reissue — fix an under/over-billed original (e.g. a stale
  // stay-total). Bills the corrected gross total instead of mirroring the original.
  const [correctAmount, setCorrectAmount] = useState(false);
  const [correctedTotal, setCorrectedTotal] = useState(String(Number(invoice?.total_debit || 0).toFixed(2)));

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const set = (k, v) => { setError(''); setForm(prev => ({ ...prev, [k]: v })); };

  const handleSubmit = async () => {
    setBusy(true); setError('');
    try {
      // Only send fields that actually differ from the original — keeps payload minimal
      // and lets the backend "undefined === mirror original" path do its job.
      const body = {};
      const origGuest = invoice?.guest_name || '';
      const origGstin = invoice?.customer_gstin || '';
      const origState = invoice?.customer_state_code || '';
      const origPos   = invoice?.place_of_supply || '';
      if ((form.guest_name || '').trim() !== origGuest) body.guest_name = (form.guest_name || '').trim();
      if ((form.customer_gstin || '').trim().toUpperCase() !== origGstin) body.customer_gstin = (form.customer_gstin || '').trim() || null;
      if ((form.customer_state_code || '').trim() !== origState) body.customer_state_code = (form.customer_state_code || '').trim() || null;
      if ((form.place_of_supply || '').trim() !== origPos) body.place_of_supply = (form.place_of_supply || '').trim() || null;

      if (correctAmount) {
        const amt = Number(correctedTotal);
        if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter a valid corrected total amount (₹, incl. tax).');
        body.corrected_debit_amount = amt;
      }

      const res = await fetch(`/api/v1/pms/invoices/${invoice.invoice_id}/reissue`, {
        method: 'POST', headers: headers(), body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Reissue failed (${res.status})`);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (result && onReissued) onReissued(result);
    onClose && onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
      <div style={{ width: 'min(600px,97vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1.25rem', position: 'relative' }}>
        <button onClick={handleClose} style={{ position: 'absolute', top: '0.5rem', right: '0.75rem', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#718096' }}>×</button>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', color: '#2d3748' }}>Issue Replacement Invoice</h2>

        <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.6rem 0.8rem', marginBottom: '1rem', fontSize: '0.88rem', color: '#4a5568' }}>
          <div><strong>Original:</strong> {invoice?.invoice_no} (CANCELLED)</div>
          <div><strong>Room:</strong> {invoice?.room_number || '-'} &nbsp; · &nbsp; <strong>Guest:</strong> {invoice?.guest_name || '-'}</div>
          <div><strong>Total:</strong> ₹{Number(invoice?.total_debit || 0).toFixed(2)} &nbsp; · &nbsp; <strong>Payment carried forward:</strong> ₹{Number(invoice?.total_credit || 0).toFixed(2)}</div>
        </div>

        {result ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '0.8rem 1rem', marginBottom: '1rem', color: '#166534' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Replacement issued ✓</div>
            <div style={{ fontSize: '0.9rem' }}>{result.message}</div>
            <div style={{ fontSize: '0.85rem', marginTop: '0.4rem' }}>
              New Invoice: <strong>{result.new_invoice_no}</strong> &nbsp;·&nbsp; Total: ₹{Number(result.total_debit || 0).toFixed(2)} &nbsp;·&nbsp; Balance: ₹{Number(result.balance || 0).toFixed(2)}
              {result.amounts_corrected && <div style={{ marginTop: '0.3rem', color: '#92400e' }}>Amount corrected to ₹{Number(result.total_debit || 0).toFixed(2)} (incl. tax).</div>}
              {result.supply_type_flipped && <div style={{ marginTop: '0.3rem', color: '#92400e' }}>Note: supply type flipped to <strong>{result.supply_type}</strong> — tax split was re-computed.</div>}
            </div>
          </div>
        ) : (
          <>
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.6rem 0.8rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#78350f' }}>
              Adjust the fields below if you need a correction on the replacement, otherwise leave them as-is to issue an identical replacement invoice. The payment from the original carries forward automatically.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.7rem' }}>
              <div>
                <label style={lblStyle}>Bill To (Guest / Company Name)</label>
                <input type="text" value={form.guest_name} onChange={e => set('guest_name', e.target.value)} style={inpStyle} />
              </div>
              <div>
                <label style={lblStyle}>Customer GSTIN (15 chars, optional)</label>
                <input type="text" maxLength={15} value={form.customer_gstin} onChange={e => set('customer_gstin', e.target.value.toUpperCase())} style={inpStyle} placeholder="e.g. 33ABCDE1234F1Z5" />
              </div>
              <div>
                <label style={lblStyle}>State Code (2-digit)</label>
                <input type="text" maxLength={2} value={form.customer_state_code} onChange={e => set('customer_state_code', e.target.value.replace(/\D/g, ''))} style={inpStyle} placeholder="e.g. 33" />
              </div>
              <div>
                <label style={lblStyle}>Place of Supply</label>
                <input type="text" value={form.place_of_supply} onChange={e => set('place_of_supply', e.target.value)} style={inpStyle} placeholder="e.g. 33-Tamil Nadu" />
              </div>
            </div>

            <div style={{ marginTop: '0.9rem', borderTop: '1px solid #edf2f7', paddingTop: '0.8rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.86rem', fontWeight: 600, color: '#2d3748', cursor: 'pointer' }}>
                <input type="checkbox" checked={correctAmount} onChange={e => { setError(''); setCorrectAmount(e.target.checked); }} />
                Correct the billed amount (fix an under/over-billed original)
              </label>
              {correctAmount && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={lblStyle}>Corrected invoice total ₹ (gross, incl. tax)</label>
                  <input type="number" min="0" step="0.01" value={correctedTotal}
                    onChange={e => { setError(''); setCorrectedTotal(e.target.value); }}
                    style={{ ...inpStyle, maxWidth: 220 }} />
                  <div style={{ fontSize: '0.76rem', color: '#92400e', marginTop: 4 }}>
                    Original was ₹{Number(invoice?.total_debit || 0).toFixed(2)}. The CGST/SGST split scales with the new total. Applies to single-charge invoices (e.g. room tariff).
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.6rem 0.8rem', marginTop: '0.8rem', color: '#b91c1c', fontSize: '0.88rem' }}>{error}</div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.1rem' }}>
          {result ? (
            <button onClick={handleClose} style={btnPrimary}>Done</button>
          ) : (
            <>
              <button onClick={handleClose} style={btnSecondary} disabled={busy}>Cancel</button>
              <button onClick={handleSubmit} style={btnPrimary} disabled={busy}>{busy ? 'Issuing...' : 'Generate Replacement Invoice'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const lblStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.2rem' };
const inpStyle = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' };
const btnPrimary = { padding: '0.55rem 1.1rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 };
const btnSecondary = { padding: '0.55rem 1.1rem', background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' };
